import React, { useEffect, useState } from 'react';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import DirectCheckoutWrapper from './direct_checkout_wrapper';
import DirectCheckoutProfileSetup from './direct_checkout_profile_setup';
import DirectCheckoutOnlyPaymentMethod from './direct_checkout_only_payment_method';
import PaymentProviderPaypalOption from '../components/PaymentProviderPaypalOption';
import ErrorMessage from '../shared/error_message';
import I18n from 'i18n-js';
import { trackEvent, MParticleEventName, MParticlePageName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { PaypalProfileProps, DirectCheckoutProfileAttributes } from '../components/payout_method_form_fields';

const PAYPAL = 'paypal';
const DIRECT_CHECKOUT = 'direct_checkout';

interface DirectCheckoutProfileProps {
  profileAttributes: DirectCheckoutProfileAttributes;
  payoutCurrencies: any;
  countryCodeOptions: string[];
  formPrefix: string;
  selected: boolean;
  shopCurrency: string;
}

interface PlaidProps {
  pendingPlaidAccountId: string;
  plaidAccountStatus: string;
  plaidEnabled: boolean;
}

interface Props {
  allowDirectCheckout: boolean;
  directCheckoutFeePercent: number;
  directCheckoutFeeFlatAmount: string;
  directCheckoutIntlFeePercent: number;
  directCheckoutProfileProps: DirectCheckoutProfileProps;
  paypalProfileProps: PaypalProfileProps;
  shopPolicyFormErrors: string[];
  formPrefix: string;
  initiallySelectedPaymentMethod: string;
  shopCurrency: string;
  allowDirectCheckoutOnly: boolean;
  plaidProps: PlaidProps;
  shopCountryCode: string;
  listingId: string;
}

// This is shown to sellers after they create a listing, before they have set up
// an accepted payment methods profile (Listings::ShopPoliciesController#new).
// It uses some of the same child components as the payment method selection
// component used on the shop policies dashboard.
export default function ShopPaymentMethodSelection(props: Props) {
  const [ selectedPaymentMethod, setSelectedPaymentMethod ] = useState(props.initiallySelectedPaymentMethod);
  const [ directCheckoutProfileAttributes, setDirectCheckoutProfileAttributes ] = useState(props.directCheckoutProfileProps.profileAttributes);

  useEffect(() => {
    trackEvent({
      eventName: MParticleEventName.ViewedPaymentSettings,
      isPlaid: props.plaidProps?.plaidEnabled,
      pageName: MParticlePageName.GetPaid,
    });
  }, []);

  const directCheckoutSelected = selectedPaymentMethod === DIRECT_CHECKOUT;

  const paypalSelected = selectedPaymentMethod === PAYPAL;

  function nameForField(fieldName) {
    return `${props.formPrefix}[${fieldName}]`;
  }

  function directCheckoutRadio() {
    if (!props.allowDirectCheckout) {
      return false;
    }

    return (
      <DirectCheckoutWrapper
        directCheckoutFeePercent={props.directCheckoutFeePercent}
        directCheckoutFeeFlatAmount={props.directCheckoutFeeFlatAmount}
        directCheckoutIntlFeePercent={props.directCheckoutIntlFeePercent}
        shopCurrency={props.shopCurrency}
        bankCountryCode={directCheckoutProfileAttributes.countryCode}
        shopCountryCode={props.shopCountryCode}
      >
        <input
          type="radio"
          value="direct_checkout"
          checked={directCheckoutSelected}
          name={nameForField('payment_method')}
          id="shop-settings-dc-radio"
          onChange={handleChangePaymentMethod}
        />
      </DirectCheckoutWrapper>
    );
  }

  function handleChangePaymentMethod(event) {
    setSelectedPaymentMethod(event.target.value);
  }

  function updateDirectCheckoutProfileCountryCode(countryCode) {
    setDirectCheckoutProfileAttributes(prevState => ({
      ...prevState,
      countryCode,
    }));
  }

  function updateDirectCheckoutProfileCurrency(currency) {
    setDirectCheckoutProfileAttributes(prevState => ({
      ...prevState,
      currency,
    }));
  }

  const directCheckoutProfileProps = {
    ...props.directCheckoutProfileProps,
    profileAttributes: directCheckoutProfileAttributes,
    selected: directCheckoutSelected,
    shopCurrency: props.shopCurrency,
    onCountryChange: updateDirectCheckoutProfileCountryCode,
    onCurrencyChange: updateDirectCheckoutProfileCurrency,
    plaidProps: props.plaidProps,
    shopCountryCode: props.shopCountryCode,
  };

  function directCheckoutProfile() {
    if (!props.allowDirectCheckout) {
      return false;
    }

    return (
      <DirectCheckoutProfileSetup {...directCheckoutProfileProps} />
    );
  }

  function shopPolicyFormErrors() {
    if (props.shopPolicyFormErrors.length === 0) {
      return false;
    }

    return (
      <ErrorMessage
        message={props.shopPolicyFormErrors.join('. ')}
        alertStyle="alert-error"
      />
    );
  }

  function paymentMethodRadios() {
    return (
      <>
        {directCheckoutRadio()}
        <PaymentProviderPaypalOption
          hasPaypalProfile={props.paypalProfileProps.hideBankAccountEditForm}
          shopCurrency={props.shopCurrency}
          inputName={nameForField('payment_method')}
          onPaymentProviderChange={handleChangePaymentMethod}
          errorMessages={props.paypalProfileProps.errorMessages}
          paypalProfileCreatedAt={props.paypalProfileProps.createdAt}
          paypalProfileFormPrefix={props.paypalProfileProps.formPrefix}
          paypalAccountLink={props.paypalProfileProps.paypalAccountLink}
          userAttributes={props.paypalProfileProps.userAttributes}
          selected={paypalSelected}
          partnerOnboarded={props.paypalProfileProps.partnerOnboarded}
          onboardingPending={props.paypalProfileProps.onboardingPending}
          userLockedForPaypalNoncompliance={props.paypalProfileProps.userLockedForPaypalNoncompliance}
          partnerReferralTrackingId={props.paypalProfileProps.partnerReferralTrackingId}
          listingId={props.listingId}
        />
      </>
    );
  }

  function directCheckoutOnly() {
    return (
      <DirectCheckoutOnlyPaymentMethod
        directCheckoutFeePercent={props.directCheckoutFeePercent}
        directCheckoutFeeFlatAmount={props.directCheckoutFeeFlatAmount}
        directCheckoutIntlFeePercent={props.directCheckoutIntlFeePercent}
        shopCurrency={props.shopCurrency}
        fieldName={nameForField('payment_method')}
        shopCountryCode={props.shopCountryCode}
      />
    );
  }

  function paymentMethodFields() {
    if (props.allowDirectCheckoutOnly) {
      return directCheckoutOnly();
    }
    return paymentMethodRadios();
  }

  function sectionHeader() {
    if (props.allowDirectCheckoutOnly) {
      return I18n.t('js.dashboard.selling.shopPolicies.yourPaymentSettings');
    }
    return I18n.t('js.dashboard.selling.shopPolicies.howCanBuyersPayYou');
  }

  return (
    <SiteModule title={sectionHeader()}>
      {shopPolicyFormErrors()}
      <div className="g-container">
        {paymentMethodFields()}
      </div>
      <div className="shop-settings-payment-setup">
        {directCheckoutProfile()}
      </div>
    </SiteModule>
  );
}
